/**
 * Permissions (step 6 hardening): what a signed-in user may DO, finer than the
 * member/admin split. A role is a bundle of permissions; an admin can add single
 * permissions to a user on top of their role ("extra grants"). Reading is never
 * a permission — every signed-in user may read everything the app shows; only
 * changes and paid/irreversible actions are gated.
 */
export const USER_ROLES = ['viewer', 'member', 'admin'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const PERMISSIONS = [
  /** Decide dedup clusters and enrichment matches one by one. */
  'crm.review',
  /** Bulk-approve the auto tier of the dedup queue. */
  'crm.bulk_approve',
  /** Start data runs: Pipedrive refresh/snapshot, dedup, ARES enrichment, segmentation. */
  'crm.run_jobs',
  /** Create and control merge jobs — irreversible Pipedrive writes in pilot/full mode. */
  'crm.merge',
  /** Create and control publish jobs (enrichment write-back to Pipedrive). */
  'crm.publish',
  /** Spend paid credits: Apollo/Lusha runs, Lusha people discovery, reveals and imports. */
  'credits.spend',
  /** Create and edit projects, units, photos, contacts; scan and monitor developer websites. */
  'projects.edit',
  /** Delete whole projects (with their photos, units and files). */
  'projects.delete',
  /** Generate and revoke self-service contact-update links. */
  'selfservice.manage',
  /** Marketing analytics: start snapshot builds and media imports, decide campaign/project mappings. */
  'performance.manage',
  /** Lead router: edit routing rules, market defaults and settings; rotate channel tokens, switch channel modes. */
  'leads.manage',
  /** Read the audit log. */
  'audit.read',
  /** Manage users: roles, extra permissions, deactivation. */
  'users.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** Short labels for permission chips and toggles. */
export const PERMISSION_LABELS: Record<Permission, string> = {
  'crm.review': 'Review duplicates & matches',
  'crm.bulk_approve': 'Bulk-approve auto tier',
  'crm.run_jobs': 'Start data runs',
  'crm.merge': 'Merge jobs',
  'crm.publish': 'Publish jobs',
  'credits.spend': 'Spend paid credits',
  'projects.edit': 'Edit projects',
  'projects.delete': 'Delete projects',
  'selfservice.manage': 'Self-service links',
  'performance.manage': 'Analytics imports & mappings',
  'leads.manage': 'Lead routing rules & channels',
  'audit.read': 'Read audit log',
  'users.manage': 'Manage users',
};

/** What each role includes by default. Viewers read; members do the daily work; admins run and configure. */
export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  viewer: [],
  member: [
    'crm.review',
    'credits.spend',
    'projects.edit',
    'projects.delete',
    'performance.manage',
    'leads.manage',
    'audit.read',
  ],
  admin: PERMISSIONS,
};

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

/**
 * The permissions a user effectively holds: the role bundle plus explicit extra
 * grants, unknown strings dropped, in canonical order, no duplicates.
 */
export function effectivePermissions(role: UserRole, extra: readonly unknown[] = []): Permission[] {
  const granted = new Set<Permission>(ROLE_PERMISSIONS[role]);
  for (const p of extra) if (isPermission(p)) granted.add(p);
  return PERMISSIONS.filter((p) => granted.has(p));
}

/** Extra grants that actually add something beyond the role — what the Users page shows as toggles. */
export function extraBeyondRole(role: UserRole, extra: readonly unknown[]): Permission[] {
  const base = new Set<Permission>(ROLE_PERMISSIONS[role]);
  return PERMISSIONS.filter((p) => !base.has(p) && extra.includes(p));
}

export function hasPermission(permissions: readonly Permission[], permission: Permission): boolean {
  return permissions.includes(permission);
}
